/**
 * Client service for Construction projects and drawing register.
 * R39 B1.a + B1.b (plan docs/plans/sector-operations.md row B1).
 * Wraps callables via callable(); components never import firebase/*.
 */

import { callable } from './callable'
import type {
  ConProject,
  ConPackage,
  ConApprovers,
  ConDrawing,
  ConDrawingRow,
  ConDrawingRevision,
  ConDrawingStatus,
  CreateConProjectInput,
  UpdateConProjectInput,
  SetConProjectPackagesInput,
  SetConProjectApproversInput,
  ListConProjectsInput,
  GetConProjectInput,
  CreateConDrawingInput,
  IssueConDrawingRevisionInput,
  WithdrawConDrawingInput,
  ListConDrawingsInput,
  ListConDrawingRevisionsInput,
} from '../types/ops'

export async function createConProject(
  input: CreateConProjectInput,
): Promise<{ projectId: string; project: ConProject; replayed?: boolean }> {
  return callable<CreateConProjectInput, { projectId: string; project: ConProject; replayed?: boolean }>(
    'createConProject',
    input,
  )
}

export async function updateConProject(
  input: UpdateConProjectInput,
): Promise<{ projectId: string; project: ConProject }> {
  return callable<UpdateConProjectInput, { projectId: string; project: ConProject }>(
    'updateConProject',
    input,
  )
}

export async function setConProjectPackages(
  input: SetConProjectPackagesInput,
): Promise<{ projectId: string; packages: ConPackage[] }> {
  return callable<SetConProjectPackagesInput, { projectId: string; packages: ConPackage[] }>(
    'setConProjectPackages',
    input,
  )
}

export async function setConProjectApprovers(
  input: SetConProjectApproversInput,
): Promise<{ projectId: string; approvers: ConApprovers }> {
  return callable<SetConProjectApproversInput, { projectId: string; approvers: ConApprovers }>(
    'setConProjectApprovers',
    input,
  )
}

export async function listConProjects(
  input: ListConProjectsInput,
): Promise<{ projects: ConProject[] }> {
  return callable<ListConProjectsInput, { projects: ConProject[] }>('listConProjects', input)
}

export async function getConProject(
  input: GetConProjectInput,
): Promise<{ project: ConProject }> {
  return callable<GetConProjectInput, { project: ConProject }>('getConProject', input)
}

export async function createConDrawing(
  input: CreateConDrawingInput,
): Promise<{ drawingId: string; revisionId: string; drawing: ConDrawing; replayed?: boolean }> {
  return callable<CreateConDrawingInput, { drawingId: string; revisionId: string; drawing: ConDrawing; replayed?: boolean }>(
    'createConDrawing',
    input,
  )
}

export async function issueConDrawingRevision(
  input: IssueConDrawingRevisionInput,
): Promise<{ drawingId: string; revisionId: string; flaggedWorkCount: number; replayed?: boolean }> {
  return callable<IssueConDrawingRevisionInput, { drawingId: string; revisionId: string; flaggedWorkCount: number; replayed?: boolean }>(
    'issueConDrawingRevision',
    input,
  )
}

export async function withdrawConDrawing(
  input: WithdrawConDrawingInput,
): Promise<{ drawingId: string; status: ConDrawingStatus }> {
  return callable<WithdrawConDrawingInput, { drawingId: string; status: ConDrawingStatus }>(
    'withdrawConDrawing',
    input,
  )
}

export async function listConDrawings(
  input: ListConDrawingsInput,
): Promise<{ drawings: ConDrawingRow[] }> {
  return callable<ListConDrawingsInput, { drawings: ConDrawingRow[] }>('listConDrawings', input)
}

export async function listConDrawingRevisions(
  input: ListConDrawingRevisionsInput,
): Promise<{ revisions: ConDrawingRevision[] }> {
  return callable<ListConDrawingRevisionsInput, { revisions: ConDrawingRevision[] }>(
    'listConDrawingRevisions',
    input,
  )
}
